import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLatest } from '../../hooks/useLatest';
import { useNavigate } from 'react-router-dom';
import { FiRefreshCw, FiSearch, FiFilter, FiEye, FiCheckCircle, FiMenu, FiDownload } from 'react-icons/fi';
import { refundsAPI, usersAPI, notificationsAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import ColumnCustomizer, { useColumnPreferences } from '../../components/Common/ColumnCustomizer';
import { UnifiedFilterHeaderCell } from '../../components/Common/UnifiedTableFilters';
import {
  runTableFilters,
  hasActiveClientTableFilters,
  CLIENT_SIDE_TABLE_FILTER_FETCH_LIMIT
} from '../../utils/tableFilters';

const REFUND_LIST_PAGE_SIZE = 50;

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'pending_approval', label: 'Pending Approval' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'processed', label: 'Processed' },
  { value: 'completed', label: 'Completed' },
  { value: 'on_hold', label: 'On Hold' }
];

const PRIORITY_OPTIONS = [
  { value: '', label: 'All Priorities' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' }
];

const statusColors = {
  pending_approval: { bg: '#fef3cd', color: '#856404' },
  under_review: { bg: '#cce5ff', color: '#004085' },
  approved: { bg: '#d4edda', color: '#155724' },
  rejected: { bg: '#f8d7da', color: '#721c24' },
  processed: { bg: '#d1ecf1', color: '#0c5460' },
  completed: { bg: '#c3e6cb', color: '#155724' },
  on_hold: { bg: '#e2e3e5', color: '#383d41' }
};

const priorityColors = { high: '#dc3545', medium: '#fd7e14', low: '#6c757d' };

const RefundRequestList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [refunds, setRefunds] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({
    search: '', status: '', priority: '', refund_type: '',
    requested_by: '', date_from: '', date_to: '', read_status: '', page: 1
  });
  const [showFilters, setShowFilters] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const dragNode = useRef(null);
  const [exporting, setExporting] = useState(false);
  const [tableFilters, setTableFilters] = useState({ globalSearch: '', columns: {} });
  const statusLabel = useCallback((value) => t(`refunds.status.${value}`, { defaultValue: String(value || '').replace(/_/g, ' ') }), [t]);
  const priorityLabel = useCallback((value) => t(`refunds.priority.${value}`, { defaultValue: value }), [t]);

  const tableColumns = useMemo(() => ([
    // Locked (always visible)
    { key: 'drag', label: '', locked: true },
    { key: 'id', label: 'Request ID', locked: true },
    { key: 'student', label: 'Student', locked: true },
    { key: 'actions', label: 'Actions', locked: true },

    // Core refund fields (default visible)
    { key: 'refund_type', label: 'Refund Type' },
    { key: 'currency', label: 'Currency' },
    { key: 'amount_refunded', label: 'Amount' },
    { key: 'status', label: 'Status' },
    { key: 'priority', label: 'Priority' },
    { key: 'initiated_by_name', label: 'Requested By' },

    // Dates / timestamps
    { key: 'issue_date', label: 'Issue Date', defaultVisible: false },        // sr.created_at
    { key: 'requested_date', label: 'Requested Date', defaultVisible: false },// sr.initiated_at
    { key: 'refunded_date', label: 'Refunded Date', defaultVisible: false },  // sr.refund_date
    { key: 'reviewed_at', label: 'Reviewed At', defaultVisible: false },      // sr.reviewed_at
    { key: 'approved_at', label: 'Approved At', defaultVisible: false },      // sr.approved_at
    { key: 'updated_at', label: 'Last Updated', defaultVisible: false },      // sr.updated_at

    // Payment / processing fields
    { key: 'refund_method', label: 'Refund Method', defaultVisible: false },
    { key: 'reference_no', label: 'Reference No.', defaultVisible: false },
    { key: 'has_receipt', label: 'Receipt', defaultVisible: false },

    // Student fields (from join)
    { key: 'student_id', label: 'Student ID', defaultVisible: false },
    { key: 'student_ref', label: 'Student Ref', defaultVisible: false },
    { key: 'student_name', label: 'Student Name', defaultVisible: false },

    // Meta / flags
    { key: 'is_read', label: 'Read', defaultVisible: false }
  ]), []);

  const { isVisible, toggleColumn, resetToDefault } = useColumnPreferences('refund_requests_list', tableColumns);
  const columnFilterDefs = useMemo(() => {
    const requestedByOptions = Array.from(new Set((users || []).map((u) => String(u.name || '')).filter(Boolean)));
    const refundTypeOptions = Array.from(new Set((refunds || []).map((r) => String(r.refund_type || '')).filter(Boolean)));
    const currencyOptions = Array.from(new Set((refunds || []).map((r) => String(r.currency || '')).filter(Boolean)));
    return [
      { key: 'id', label: 'ID', field: 'refund_id', type: 'text' },
      { key: 'student', label: 'Student', field: 'student_ref', type: 'text' },
      { key: 'student_id', label: 'Student ID', field: 'student_id', type: 'text' },
      { key: 'student_ref', label: 'Student Ref', field: 'student_ref', type: 'text' },
      { key: 'student_name', label: 'Student Name', field: 'first_name', type: 'text' },
      { key: 'refund_type', label: 'Type', field: 'refund_type', type: 'multi', options: refundTypeOptions.map((v) => ({ value: v, label: String(v).replace(/_/g, ' ') })) },
      { key: 'currency', label: 'Currency', field: 'currency', type: 'multi', options: currencyOptions.map((v) => ({ value: v, label: v })) },
      { key: 'amount_refunded', label: 'Amount', field: 'amount_refunded', type: 'text' },
      { key: 'initiated_by_name', label: 'Requested By', field: 'initiated_by_name', type: 'multi', options: requestedByOptions.map((v) => ({ value: v, label: v })) },
      { key: 'issue_date', label: 'Issue Date', field: 'created_at', type: 'date' },
      { key: 'requested_date', label: 'Requested Date', field: 'initiated_at', type: 'date' },
      { key: 'refunded_date', label: 'Refunded Date', field: 'refund_date', type: 'date' },
      { key: 'refund_method', label: 'Method', field: 'refund_method', type: 'text' },
      { key: 'reference_no', label: 'Reference', field: 'reference_no', type: 'text' },
      { key: 'has_receipt', label: 'Receipt', field: 'refund_receipt_copy', type: 'multi', options: [{ value: 'true', label: t('common.yes', 'Yes') }, { value: 'false', label: t('common.no', 'No') }] },
      { key: 'priority', label: 'Priority', field: 'priority', type: 'multi', options: PRIORITY_OPTIONS.filter((o) => o.value).map((o) => ({ value: o.value, label: priorityLabel(o.value) })) },
      { key: 'status', label: 'Status', field: 'status', type: 'multi', options: STATUS_OPTIONS.filter((o) => o.value).map((o) => ({ value: o.value, label: statusLabel(o.value) })) },
      { key: 'reviewed_at', label: 'Reviewed', field: 'reviewed_at', type: 'date' },
      { key: 'approved_at', label: 'Approved', field: 'approved_at', type: 'date' },
      { key: 'updated_at', label: 'Updated', field: 'updated_at', type: 'date' },
      { key: 'is_read', label: 'Read', field: 'is_read', type: 'multi', options: [{ value: '1', label: t('refunds.read', 'Read') }, { value: '0', label: t('refunds.unread', 'Unread') }] }
    ];
  }, [refunds, users, t, priorityLabel, statusLabel]);
  const columnFilterDefsRef = useLatest(columnFilterDefs);
  const columnFilterMap = useMemo(
    () => Object.fromEntries(columnFilterDefs.map((d) => [d.key, d])),
    [columnFilterDefs]
  );
  const updateColumnFilters = useCallback(
    (updater) => setTableFilters((prev) => ({ ...prev, columns: updater(prev.columns || {}) })),
    []
  );
  const normalizedRows = useMemo(
    () =>
      refunds.map((r) => ({
        ...r,
        first_name: `${r.first_name || ''} ${r.last_name || ''}`.trim(),
        refund_receipt_copy: r.refund_receipt_copy ? 'true' : 'false',
        is_read: r.is_read ? '1' : '0'
      })),
    [refunds]
  );
  const filteredRefunds = useMemo(
    () =>
      runTableFilters(
        normalizedRows,
        tableFilters,
        tableFilters,
        ['refund_id', 'student_ref', 'first_name', 'refund_type', 'reference_no', 'initiated_by_name', 'status'],
        columnFilterDefs
      ),
    [normalizedRows, tableFilters, columnFilterDefs]
  );
  const clientTableFilterMode = useMemo(
    () => hasActiveClientTableFilters(tableFilters, columnFilterDefs),
    [tableFilters, columnFilterDefs]
  );
  const pagedFilteredRefunds = useMemo(() => {
    if (!clientTableFilterMode) return filteredRefunds;
    const start = (filters.page - 1) * REFUND_LIST_PAGE_SIZE;
    return filteredRefunds.slice(start, start + REFUND_LIST_PAGE_SIZE);
  }, [clientTableFilterMode, filteredRefunds, filters.page]);
  const listTotalForPaging = clientTableFilterMode ? filteredRefunds.length : total;

  useEffect(() => {
    const tp = Math.max(1, Math.ceil(listTotalForPaging / REFUND_LIST_PAGE_SIZE));
    if (filters.page > tp) setFilters((f) => ({ ...f, page: tp }));
  }, [clientTableFilterMode, filteredRefunds.length, total, filters.page]);

  const loadRefunds = useCallback(async () => {
    setLoading(true);
    try {
      const active = clientTableFilterMode;
      const params = { ...filters, limit: active ? CLIENT_SIDE_TABLE_FILTER_FETCH_LIMIT : REFUND_LIST_PAGE_SIZE, page: active ? 1 : filters.page };
      Object.keys(params).forEach((k) => { if (params[k] === '' || params[k] == null) delete params[k]; });
      const res = await refundsAPI.getAll(params);
      setRefunds(res.data.refunds || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error('Failed to load refund requests');
    }
    setLoading(false);
  }, [filters, clientTableFilterMode]);

  useEffect(() => { loadRefunds(); }, [loadRefunds]);

  const exportColumns = useMemo(() => {
    // Export everything except purely-UI columns
    return tableColumns.filter((c) => !['drag', 'actions'].includes(c.key));
  }, [tableColumns]);

  const toCsvCell = (val) => {
    if (val === null || val === undefined) return '""';
    const s = String(val).replace(/"/g, '""');
    return `"${s}"`;
  };

  const fmtDate = (d) => {
    if (!d) return '';
    try {
      return new Date(d).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
    } catch {
      return String(d);
    }
  };

  const getExportValue = (r, key) => {
    switch (key) {
      case 'id': return r.refund_id;
      case 'student': return r.student_ref;
      case 'refund_type': return r.refund_type;
      case 'currency': return r.currency;
      case 'amount_refunded': return r.amount_refunded;
      case 'status': return r.status;
      case 'priority': return r.priority;
      case 'initiated_by_name': return r.initiated_by_name;
      case 'issue_date': return fmtDate(r.created_at);
      case 'requested_date': return fmtDate(r.initiated_at || r.created_at);
      case 'refunded_date': return r.refund_date ? fmtDate(r.refund_date) : '';
      case 'reviewed_at': return fmtDate(r.reviewed_at);
      case 'approved_at': return fmtDate(r.approved_at);
      case 'updated_at': return fmtDate(r.updated_at);
      case 'refund_method': return r.refund_method;
      case 'reference_no': return r.reference_no;
      case 'has_receipt': return r.refund_receipt_copy ? 'YES' : 'NO';
      case 'student_id': return r.student_id;
      case 'student_ref': return r.student_ref;
      case 'student_name': return `${r.first_name || ''} ${r.last_name || ''}`.trim();
      case 'is_read': return r.is_read ? 'READ' : 'UNREAD';
      default:
        // fallback: export raw field if it exists
        return r[key];
    }
  };

  const handleExportAll = async () => {
    setExporting(true);
    try {
      const baseParams = { ...filters };
      delete baseParams.page;
      // fetch all pages
      const all = [];
      let page = 1;
      const limit = 500;
      let totalExpected = null;

      while (true) {
        const params = { ...baseParams, page, limit };
        Object.keys(params).forEach((k) => { if (!params[k]) delete params[k]; });
        const res = await refundsAPI.getAll(params);
        const rows = res.data?.refunds || [];
        if (totalExpected == null) totalExpected = Number(res.data?.total || 0);
        all.push(...rows);
        if (rows.length < limit) break;
        if (totalExpected && all.length >= totalExpected) break;
        page += 1;
        if (page > 200) break; // safety
      }

      const headers = exportColumns.map((c) => c.label || c.key);
      const csvRows = all.map((r) => exportColumns.map((c) => toCsvCell(getExportValue(r, c.key))).join(','));
      const csv = [headers.map(toCsvCell).join(','), ...csvRows].join('\n');

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const today = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `refund_requests_export_${today}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${all.length} refund request(s)`);
    } catch (err) {
      toast.error('Export failed');
    }
    setExporting(false);
  };

  const handleDragStart = (e, idx) => {
    setDragIdx(idx);
    dragNode.current = e.target.closest('tr');
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', idx);
    setTimeout(() => { if (dragNode.current) dragNode.current.style.opacity = '0.4'; }, 0);
  };

  const handleDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (idx !== overIdx) setOverIdx(idx);
  };

  const handleDrop = (e, dropIdx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === dropIdx) {
      setDragIdx(null);
      setOverIdx(null);
      return;
    }
    setRefunds((prev) => {
      const copy = [...prev];
      const [moved] = copy.splice(dragIdx, 1);
      copy.splice(dropIdx, 0, moved);
      return copy;
    });
    setDragIdx(null);
    setOverIdx(null);
  };

  const handleDragEnd = () => {
    if (dragNode.current) dragNode.current.style.opacity = '1';
    dragNode.current = null;
    setDragIdx(null);
    setOverIdx(null);
  };

  useEffect(() => {
    usersAPI.getAll({ limit: 500 }).then((r) => setUsers(r.data?.users || r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    refundsAPI.markAllRead().catch(() => {});
    window.dispatchEvent(new Event('refund-mark-read'));
  }, []);

  const handleMarkAllRead = async () => {
    try {
      await refundsAPI.markAllRead();
      toast.success('All marked as read');
      window.dispatchEvent(new Event('refund-mark-read'));
      loadRefunds();
    } catch (_) {}
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Refund Requests</h1>
          <p className="page-subtitle">{clientTableFilterMode ? listTotalForPaging : total} request{(clientTableFilterMode ? listTotalForPaging : total) !== 1 ? 's' : ''}</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <ColumnCustomizer
            columns={tableColumns}
            isVisible={isVisible}
            toggleColumn={toggleColumn}
            resetToDefault={resetToDefault}
          />
          <button className="btn btn-outline btn-sm" onClick={handleExportAll} disabled={exporting} title="Export CSV (all rows)">
            <FiDownload /> {exporting ? 'Exporting...' : 'Export CSV'}
          </button>
          <button className="btn btn-outline btn-sm" onClick={handleMarkAllRead}>
            <FiCheckCircle /> Mark All Read
          </button>
          <button className="btn btn-outline btn-sm" onClick={() => setShowFilters(!showFilters)}>
            <FiFilter /> Filters
          </button>
          <button className="btn btn-outline btn-sm" onClick={loadRefunds}>
            <FiRefreshCw />
          </button>
        </div>
      </div>

      {/* Search & Quick Filters */}
      <div className="filter-bar" style={{ marginBottom: 16, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div className="search-box" style={{ position: 'relative', flex: 1, minWidth: 220 }}>
          <FiSearch style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', zIndex: 1, pointerEvents: 'none' }} />
          <input
            type="text"
            placeholder="Search by student name, ref, reference number..."
            value={tableFilters.globalSearch}
            onChange={(e) => setTableFilters((prev) => ({ ...prev, globalSearch: e.target.value }))}
          />
        </div>
        <div className="filter-group">
          <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}>
            {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.value ? statusLabel(o.value) : t('refunds.allStatuses', 'All Statuses')}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <select value={filters.priority} onChange={(e) => setFilters({ ...filters, priority: e.target.value, page: 1 })}>
            {PRIORITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.value ? priorityLabel(o.value) : t('refunds.allPriorities', 'All Priorities')}</option>)}
          </select>
        </div>
      </div>

      {/* Expanded filters */}
      {showFilters && (
        <div className="card" style={{ marginBottom: 16, padding: 0, overflow: 'hidden' }}>
          <div className="filters-grid">
            <div className="filter-group">
              <label>Refund Type</label>
              <select value={filters.refund_type} onChange={(e) => setFilters({ ...filters, refund_type: e.target.value, page: 1 })}>
                <option value="">{t('refunds.allTypes', 'All Types')}</option>
                <option value="application_fee">{t('refunds.types.application_fee', 'Application Fee')}</option>
                <option value="tuition_fee">{t('refunds.types.tuition_fee', 'Tuition Fee')}</option>
                <option value="registration_fee">{t('refunds.types.registration_fee', 'Registration Fee')}</option>
                <option value="visa_fee">{t('refunds.types.visa_fee', 'Visa Fee')}</option>
                <option value="other">{t('refunds.types.other', 'Other')}</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Requested By</label>
              <select value={filters.requested_by} onChange={(e) => setFilters({ ...filters, requested_by: e.target.value, page: 1 })}>
                <option value="">All Users</option>
                {users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
              </select>
            </div>
            <div className="filter-group">
              <label>{t('refunds.readUnread', 'Read/Unread')}</label>
              <select value={filters.read_status} onChange={(e) => setFilters({ ...filters, read_status: e.target.value, page: 1 })}>
                <option value="">{t('common.all', 'All')}</option>
                <option value="unread">{t('refunds.unread', 'Unread')}</option>
                <option value="read">{t('refunds.read', 'Read')}</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Date From</label>
              <input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value, page: 1 })} />
            </div>
            <div className="filter-group">
              <label>Date To</label>
              <input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value, page: 1 })} />
            </div>
          </div>
          <div style={{ padding: '0 16px 16px' }}>
            <button className="btn btn-outline btn-sm" onClick={() => setFilters({ search: '', status: '', priority: '', refund_type: '', requested_by: '', date_from: '', date_to: '', read_status: '', page: 1 })}>
              Clear Filters
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="loading-spinner"><div className="spinner" /></div>
      ) : filteredRefunds.length === 0 ? (
        <div className="card"><div className="empty-state"><p>No refund requests found</p></div></div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="table-responsive">
            <table>
              <thead>
                <tr>
                  {isVisible('drag') && <th style={{ width: 36 }} />}
                  {isVisible('id') && <UnifiedFilterHeaderCell column={columnFilterMap.id} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('student') && <UnifiedFilterHeaderCell column={columnFilterMap.student} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('student_id') && <UnifiedFilterHeaderCell column={columnFilterMap.student_id} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('student_ref') && <UnifiedFilterHeaderCell column={columnFilterMap.student_ref} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('student_name') && <UnifiedFilterHeaderCell column={columnFilterMap.student_name} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}

                  {isVisible('refund_type') && <UnifiedFilterHeaderCell column={columnFilterMap.refund_type} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('currency') && <UnifiedFilterHeaderCell column={columnFilterMap.currency} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('amount_refunded') && <UnifiedFilterHeaderCell column={columnFilterMap.amount_refunded} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('initiated_by_name') && <UnifiedFilterHeaderCell column={columnFilterMap.initiated_by_name} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}

                  {isVisible('issue_date') && <UnifiedFilterHeaderCell column={columnFilterMap.issue_date} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('requested_date') && <UnifiedFilterHeaderCell column={columnFilterMap.requested_date} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('refunded_date') && <UnifiedFilterHeaderCell column={columnFilterMap.refunded_date} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}

                  {isVisible('refund_method') && <UnifiedFilterHeaderCell column={columnFilterMap.refund_method} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('reference_no') && <UnifiedFilterHeaderCell column={columnFilterMap.reference_no} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('has_receipt') && <UnifiedFilterHeaderCell column={columnFilterMap.has_receipt} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}

                  {isVisible('priority') && <UnifiedFilterHeaderCell column={columnFilterMap.priority} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('status') && <UnifiedFilterHeaderCell column={columnFilterMap.status} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('reviewed_at') && <UnifiedFilterHeaderCell column={columnFilterMap.reviewed_at} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('approved_at') && <UnifiedFilterHeaderCell column={columnFilterMap.approved_at} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('updated_at') && <UnifiedFilterHeaderCell column={columnFilterMap.updated_at} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('is_read') && <UnifiedFilterHeaderCell column={columnFilterMap.is_read} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />}
                  {isVisible('actions') && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {pagedFilteredRefunds.map((r, idx) => {
                  const sc = statusColors[r.status] || statusColors.on_hold;
                  const isUnread = !r.is_read;
                  const isDragging = dragIdx === idx;
                  const isOver = overIdx === idx && dragIdx !== idx;
                  return (
                    <tr
                      key={r.refund_id}
                      draggable={!clientTableFilterMode}
                      onDragStart={!clientTableFilterMode ? (e) => handleDragStart(e, idx) : undefined}
                      onDragOver={!clientTableFilterMode ? (e) => handleDragOver(e, idx) : undefined}
                      onDrop={!clientTableFilterMode ? (e) => handleDrop(e, idx) : undefined}
                      onDragEnd={!clientTableFilterMode ? handleDragEnd : undefined}
                      style={{
                        background: isOver
                          ? 'var(--primary-50, #eff6ff)'
                          : isUnread
                            ? 'rgba(79,70,229,0.04)'
                            : undefined,
                        cursor: 'pointer',
                        fontWeight: isUnread ? 600 : 400,
                        opacity: isDragging ? 0.4 : 1,
                        borderTop: isOver && dragIdx > idx ? '2px solid var(--primary)' : undefined,
                        borderBottom: isOver && dragIdx < idx ? '2px solid var(--primary)' : undefined,
                        transition: 'background 0.15s ease'
                      }}
                      onClick={() => navigate(`/refund-requests/${r.refund_id}`)}
                    >
                      {isVisible('drag') && (
                        <td
                          style={{ width: 36, textAlign: 'center', cursor: 'grab', padding: '0 4px', color: 'var(--text-light, #94a3b8)' }}
                          onMouseDown={(e) => e.stopPropagation()}
                          title="Drag to reorder"
                        >
                          <FiMenu size={14} />
                        </td>
                      )}
                      {isVisible('id') && (
                        <td style={{ fontWeight: 700, fontSize: 13 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            {isUnread && <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--primary)', flexShrink: 0 }} />}
                            #{r.refund_id}
                          </div>
                        </td>
                      )}
                      {isVisible('student') && (
                        <td>
                          <button
                            style={{
                              background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                              textAlign: 'left', color: 'var(--primary)', fontWeight: 600, fontSize: 13
                            }}
                            onClick={(e) => { e.stopPropagation(); navigate(`/students/${r.student_id}`); }}
                            title="View student details"
                          >
                            {r.student_ref}
                          </button>
                          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{r.first_name} {r.last_name}</div>
                        </td>
                      )}
                      {isVisible('student_id') && <td style={{ fontSize: 12, fontWeight: 600 }}>#{r.student_id}</td>}
                      {isVisible('student_ref') && <td style={{ fontSize: 13, fontWeight: 600 }}>{r.student_ref || '-'}</td>}
                      {isVisible('student_name') && <td style={{ fontSize: 13 }}>{`${r.first_name || ''} ${r.last_name || ''}`.trim() || '-'}</td>}

                      {isVisible('refund_type') && (
                        <td style={{ fontSize: 13 }}>{String(r.refund_type || '').replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}</td>
                      )}
                      {isVisible('currency') && <td>{r.currency}</td>}
                      {isVisible('amount_refunded') && (
                        <td style={{ fontWeight: 700 }}>
                          {r.currency} {parseFloat(r.amount_refunded || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                      )}
                      {isVisible('initiated_by_name') && <td style={{ fontSize: 13 }}>{r.initiated_by_name || '-'}</td>}

                      {isVisible('issue_date') && (
                        <td style={{ fontSize: 12 }}>
                          {r.created_at ? new Date(r.created_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : '-'}
                        </td>
                      )}
                      {isVisible('requested_date') && (
                        <td style={{ fontSize: 12 }}>
                          {(r.initiated_at || r.created_at) ? new Date(r.initiated_at || r.created_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : '-'}
                        </td>
                      )}
                      {isVisible('refunded_date') && (
                        <td style={{ fontSize: 12 }}>
                          {r.refund_date ? new Date(r.refund_date).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : '—'}
                        </td>
                      )}

                      {isVisible('refund_method') && (
                        <td style={{ fontSize: 12 }}>
                          {r.refund_method ? String(r.refund_method).replace(/_/g, ' ') : '—'}
                        </td>
                      )}
                      {isVisible('reference_no') && (
                        <td style={{ fontSize: 12, fontFamily: 'monospace' }}>
                          {r.reference_no || '—'}
                        </td>
                      )}
                      {isVisible('has_receipt') && (
                        <td style={{ fontSize: 12, fontWeight: 700, color: r.refund_receipt_copy ? 'var(--success, #10B981)' : 'var(--text-muted)' }}>
                          {r.refund_receipt_copy ? t('common.yes', 'Yes') : t('common.no', 'No')}
                        </td>
                      )}

                      {isVisible('priority') && (
                        <td>
                          <span style={{ fontWeight: 700, fontSize: 11, color: priorityColors[r.priority] || '#6c757d' }}>
                            {(r.priority || 'medium').toUpperCase()}
                          </span>
                        </td>
                      )}
                      {isVisible('status') && (
                        <td>
                          <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700, background: sc.bg, color: sc.color }}>
                            {(r.status || '').replace(/_/g, ' ').toUpperCase()}
                          </span>
                        </td>
                      )}
                      {isVisible('reviewed_at') && (
                        <td style={{ fontSize: 12 }}>{r.reviewed_at ? new Date(r.reviewed_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : '—'}</td>
                      )}
                      {isVisible('approved_at') && (
                        <td style={{ fontSize: 12 }}>{r.approved_at ? new Date(r.approved_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : '—'}</td>
                      )}
                      {isVisible('updated_at') && (
                        <td style={{ fontSize: 12 }}>{r.updated_at ? new Date(r.updated_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : '—'}</td>
                      )}
                      {isVisible('is_read') && (
                        <td style={{ fontSize: 12, fontWeight: 700, color: r.is_read ? 'var(--success, #10B981)' : 'var(--warning, #F59E0B)' }}>
                          {r.is_read ? 'READ' : 'UNREAD'}
                        </td>
                      )}
                      {isVisible('actions') && (
                        <td>
                          <button className="btn btn-sm btn-outline" onClick={(e) => { e.stopPropagation(); navigate(`/refund-requests/${r.refund_id}`); }}>
                            <FiEye size={14} /> View
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {listTotalForPaging > REFUND_LIST_PAGE_SIZE && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: 16 }}>
              <button className="btn btn-outline btn-sm" disabled={filters.page <= 1} onClick={() => setFilters({ ...filters, page: filters.page - 1 })}>Previous</button>
              <span style={{ padding: '6px 12px', fontSize: 13 }}>Page {filters.page} of {Math.ceil(listTotalForPaging / REFUND_LIST_PAGE_SIZE)}</span>
              <button className="btn btn-outline btn-sm" disabled={filters.page >= Math.ceil(listTotalForPaging / REFUND_LIST_PAGE_SIZE)} onClick={() => setFilters({ ...filters, page: filters.page + 1 })}>Next</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RefundRequestList;
